import React from "react"
import { Box, Checkbox, Typography, useMediaQuery, useTheme } from "@mui/material"
import { GalleryDateGroup, GalleryImage } from "@/features/gallery/hooks/useGalleryImages"
import GalleryImageTile from "@/features/gallery/components/GalleryImageTile"

interface Props {
  groups: GalleryDateGroup[]
  isSelected: (img: GalleryImage) => boolean
  isDateAllSelected: (images: GalleryImage[]) => boolean
  isDateSomeSelected: (images: GalleryImage[]) => boolean
  onSelect: (img: GalleryImage) => void
  onToggleDate: (images: GalleryImage[]) => void
  onOpen: (img: GalleryImage, allImages: GalleryImage[]) => void
}

const GalleryImageGrid: React.FC<Props> = ({
  groups,
  isSelected,
  isDateAllSelected,
  isDateSomeSelected,
  onSelect,
  onToggleDate,
  onOpen
}) => {
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.only("xs"))
  const isSm = useMediaQuery(theme.breakpoints.only("sm"))

  const cols = isXs ? 2 : isSm ? 3 : 4
  const tileSize = `calc((100% - ${(cols - 1) * 2}px) / ${cols})`

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      {groups.map(group => (
        <Box key={group.dateLabel} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.5,
              px: { xs: 1, sm: 0 }
            }}>
            <Checkbox
              size="small"
              checked={isDateAllSelected(group.images)}
              indeterminate={isDateSomeSelected(group.images)}
              onChange={() => onToggleDate(group.images)}
              sx={{ p: 0.5 }}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ letterSpacing: 0.3, cursor: "pointer", userSelect: "none" }}
              onClick={() => onToggleDate(group.images)}>
              {group.dateLabel}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, ${tileSize})`,
              gap: "2px"
            }}>
            {group.images.map(img => (
              <GalleryImageTile
                key={img.assetId}
                image={img}
                selected={isSelected(img)}
                onSelect={onSelect}
                onClick={clickedImg => onOpen(clickedImg, group.images)}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default GalleryImageGrid
